import { useState, useEffect, useRef } from 'react';
import { Consultant, ConsultantFormData } from '../models/consultant';
import { fetchConsultants, createConsultant, updateConsultant, deleteConsultant } from '../api/consultant';
import { useSelector } from 'react-redux';
import { getUserProfile } from '../configuration/login/loginSlice';
import { fetchUserId } from '../api/fetchUser';

export const useConsultants = () => {
  const userProfile = useSelector(getUserProfile);

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState<Consultant | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [consultantToDelete, setConsultantToDelete] = useState<Consultant | null>(null);

  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  // Drag to scroll logic
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: any) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const loadConsultants = async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      if (!userId) {
        setUserHasAccess(false);
        return;
      }

      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      const isAuthorized = authorizedUserIds.includes(userId);
      if (!isAuthorized) {
        setUserHasAccess(false);
        return;
      }

      setUserHasAccess(true);

      const data = await fetchConsultants({ page: 0, limit: 500 });
      setConsultants([...data]);
    } catch (err) {
      setError('Erro ao carregar consultores');
      console.error('Error loading consultants:', err);
    } finally {
      setLoading(false);
    }
  };

  const addConsultant = async (data: ConsultantFormData): Promise<boolean> => {
    setError(null);
    
    try {
      const newConsultant = await createConsultant(data);
      if (newConsultant) {
        setConsultants([...consultants, newConsultant]);
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao criar consultor');
      console.error('Error creating consultant:', err);
      return false;
    }
  };

  const editConsultant = async (id: string, data: ConsultantFormData): Promise<boolean> => {
    setError(null);
    
    try {
      const updatedConsultant = await updateConsultant(id, data);
      if (updatedConsultant) {
        setConsultants(consultants.map(consultant => consultant.id === id ? updatedConsultant : consultant));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao atualizar consultor');
      console.error('Error updating consultant:', err);
      return false;
    }
  };

  const removeConsultant = async (id: string): Promise<boolean> => {
    setError(null);
    setDeleteLoading(true);
    try {
      const success = await deleteConsultant(id);
      if (success) {
        setConsultants(consultants.filter(consultant => consultant.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao excluir consultor');
      console.error('Error deleting consultant:', err);
      return false;
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    loadConsultants();
  }, []);

  const handleEdit = (consultant: Consultant) => {
    setEditingConsultant(consultant);
    setShowForm(true);
  };

  const handleDelete = (consultant: Consultant) => {
    setConsultantToDelete(consultant);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (consultantToDelete?.id) {
      await removeConsultant(consultantToDelete.id);
      setShowDeleteDialog(false);
      setConsultantToDelete(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingConsultant(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingConsultant(null);
  };

  return {
    consultants,
    loading,
    deleteLoading,
    error,
    loadConsultants,
    addConsultant,
    editConsultant,
    removeConsultant,
    showForm,
    editingConsultant,
    showDeleteDialog,
    consultantToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    userHasAccess,
    scrollContainerRef,
    isDragging,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove,
  };
};

